import React, { useEffect, useState } from 'react';
import { ENABLE_DEVELOPER_LINKS, ENABLE_PCO_INTEGRATION, ORG_PATHS } from '../constants';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { orgMenuLinks } from './header/account-menu/menuLinks';
import DownChevron from '../../../img/sprite/left-chevron-blue.svg';
import { connect, useSelector } from 'react-redux';
import { getViewTransactionHistoryAccess } from '../selectors/OrgManagementSelectors';
import { matchPath } from 'react-router';
import PropTypes from 'prop-types';
import OrderHistoryDropdown from './OrderHistoryDropdown';
import BillingAccountsDropdown from './BillingAccountsDropdown';
import { injectParams } from '../utils/pathHelpers';
import OrgIcon from '../../../img/sprite/org-user-icon.svg';
import css from '../../../css/css-variables';
import { HeaderWrapper, SectionHeader, StyledSvg } from './SideNavIndividual';
import { fontResize } from '../utils/formUtils';
import GroupDropdown from './GroupDropdown';

export const StyledChevron = styled.svg`
  padding-right: 8px;
  height: 14px;
  width: 14px;
  display: block !important;
  ${({ transform }) => transform && `transform: ${transform}`};
`;

const StyledLink = styled(Link)`
  border: none;
  font-size: 16px;
  color: ${css.colors.Progress};
  text-decoration: none;
  ${({activeLink}) => activeLink && 'pointer-events: none'};
  font-weight: ${({bold}) => bold ? 'bold' : 'normal'};
  padding: 8px 0;

  &:hover {
    text-decoration: ${({activeLink}) => activeLink ? 'none' : 'underline'};
  }
`;
export const AccountLink = styled(Link)`
  border: none;
  font-size: 16px;
  color: #476692;
  text-decoration: none;
  ${({activeLink}) => activeLink && 'pointer-events: none'};
  font-weight: ${({bold}) => bold ? 'bold' : 'normal'};
  padding: 8px 0;

  &:hover {
    text-decoration: ${({activeLink}) => activeLink ? 'none' : 'underline'};
  }
`;

export const GroupTitle = styled.div`
  font-size: 11px;
  color: #999999; 
  text-decoration: none;
  font-style: normal;
  text-transform: uppercase;
  font-weight: bold;
  padding: 8px 0;
`;

export const LinkContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

function SideNavOrg({ selectedOrg, updatedOrgMenuLinks, pathname, organizationId }) {

  const billingAccounts = selectedOrg?.organizationProfile?.billingAccounts;
  const firstAccount = selectedOrg?.organizationProfile?.billingAccounts?.[0]?.id;
  const [showBillingAccounts, setShowBillingAccounts] = useState(false);
  const [showOrderAccounts, setShowOrderAccounts] = useState(false);
  const [showPCOGroups, setShowPCOGroups] = useState(false);
  const [activeBillingAccount, setActiveBillingAccount] = useState(null);

  useEffect(() => {
    // Split the URL by '/' to get an array of segments
    const segments = pathname.split('/');

    // Find the index of 'billing-activity' in the array
    const baIndex = pathname.includes('billing-activity') ? segments.indexOf('billing-activity') : segments.indexOf('order-history')

    if (baIndex !== -1 && baIndex < segments.length - 1) {
      // Extract the parameter value that follows 'billing-activity'
      const param = segments[baIndex + 1];
      setActiveBillingAccount(param);
    }
  }, [pathname]);

  //Show or hide billing account and order history dropdown components
  useEffect(() => {
    if (pathname.includes('billing')) {
      setShowBillingAccounts(true);
      setShowOrderAccounts(false);
      setShowPCOGroups(false);
    } else if (pathname.includes('order-history')) {
      setShowOrderAccounts(true);
      setShowBillingAccounts(false);
      setShowPCOGroups(false);
    } else if (pathname.includes('groups' || 'pco-groups')) {
      setShowPCOGroups(true);
      setShowOrderAccounts(false);
      setShowBillingAccounts(false);
    } else {
      setShowBillingAccounts(false);
      setShowOrderAccounts(false);
      setShowPCOGroups(false);
    }
  }, [pathname]);

  //Filter billingAccounts that are active
  const filteredActiveAccounts = billingAccounts?.filter(account => account.active === true);

  const defaultPath = ORG_PATHS.DASHBOARD.replace(':organizationId', selectedOrg?.organizationId);

  const handleTransactionHistoryAccess = useSelector(
    state => getViewTransactionHistoryAccess(
      state, selectedOrg?.organizationId, filteredActiveAccounts?.map(account => account.accountNumber)
    )
  );

  const accountsWithPermission = handleTransactionHistoryAccess.filter(permission => permission.hasAccess === true).map(permission => permission.permissionScope.account);

  const activeAccountsWithPermission = filteredActiveAccounts?.filter(
    account => accountsWithPermission.includes(account.accountNumber)
  );

  const hasTransactionHistoryAccess = handleTransactionHistoryAccess.some(permission => permission.hasAccess === true);

  //Set activeBillingAccount to firstAccount to prevent url from showing undefined
  useEffect(() => {
    if (activeAccountsWithPermission) {
      setActiveBillingAccount(activeAccountsWithPermission?.[0]?.id)
    }
  }, [activeAccountsWithPermission, firstAccount]);


  const hasBillingAccount = activeAccountsWithPermission?.length === 1;
  const hasMultipleAccounts = activeAccountsWithPermission?.length > 1;

  const handleBillingAccountId = () => {
    if(hasBillingAccount) {
      return activeAccountsWithPermission?.[0].id
    } else if (hasMultipleAccounts) {
      return activeBillingAccount
    } else {
      return ''
    }
  };

  const handleOrgUrl = (url) => {
    if (url?.includes('order')) {
      return injectParams(url, { organizationId: selectedOrg?.organizationId, billingAccountId: handleBillingAccountId() })
    } else if (url?.includes('billing')) {
      return injectParams(url, { organizationId: selectedOrg?.organizationId, billingAccountId: handleBillingAccountId() })
    } else {
      return url.replace(':organizationId', selectedOrg?.organizationId)
    }
  };

  const formatLinkName = (linkName) => {
    switch (linkName) {
      case 'Order History':
        return 'order-history';
      case 'Subscriptions':
        return 'subscriptions';
      case 'Autoship':
        return 'autoship';
      case 'Billing':
        return 'billing-activity';
      case 'Organization Profile':
        return 'profile';
      case 'Addresses':
        return 'shipping-addresses';
      case 'Groups (Staff)':
        return 'groups';
    }
  };

  const LinkName = ({ name }) => {
    const baActive = pathname.includes('billing-activity');
    const orderHistoryActive = pathname.includes('order-history');

    return (
      <LinkContainer>
        {name}
        {name === 'Billing' && hasMultipleAccounts &&
          <svg style={{ transform: baActive ? 'none' : 'rotate(270deg)' }}>
            <use href={`#${DownChevron.id}`} xlinkHref={`#${DownChevron.id}`} />
          </svg>
        }
        {name === 'Order History' && hasMultipleAccounts &&
          <svg style={{ transform: orderHistoryActive ? 'none' : 'rotate(270deg)' }}>
            <use href={`#${DownChevron.id}`} xlinkHref={`#${DownChevron.id}`} />
          </svg>
        }
      </LinkContainer>
    )
  };

  const hasIcon = selectedOrg?.orgIcon;

  const filteredMenuLinks = (!hasTransactionHistoryAccess ? updatedOrgMenuLinks : orgMenuLinks)?.linkGroup.filter(
    group => group.title !== 'Developer Links' || ENABLE_DEVELOPER_LINKS
  );


  return (
    <>
      <div>
        <HeaderWrapper>
          <StyledSvg flexShrink="0">
            <use href={`#${hasIcon ? selectedOrg?.orgIcon?.id : OrgIcon?.id}`} />
          </StyledSvg>
          <SectionHeader fontSize={fontResize(selectedOrg?.organizationName)} bold={true}>
            {selectedOrg?.organizationName}
          </SectionHeader>
        </HeaderWrapper>
        <div style={{padding: '0 0 7px 0'}}>
          <StyledLink to={ORG_PATHS.DASHBOARD.replace(
            ':organizationId',
            selectedOrg?.organizationId
          )}
            style={{
            fontSize: '14px',
            color: pathname === defaultPath ? '#414042' : css.colors.Progress,
            fontWeight: pathname === defaultPath ? 'bold' : 'normal'
          }}
            activeLink={pathname === ORG_PATHS.DASHBOARD.replace(
              ':organizationId',
              selectedOrg?.organizationId
            )}
          >
            Home
          </StyledLink>
        </div>
        {filteredMenuLinks.map((group, index) => {
          const isLastChild = filteredMenuLinks.length - 1 === index;
          return (
          <>
            {group?.title && <GroupTitle key={index} title={group?.title} isLastChild={isLastChild}>{group?.title}</GroupTitle>}
            {group?.links.map((link, index) => {
              const isLastChild = group?.links.length -1 === index;
              const isActiveLink = pathname.includes(formatLinkName(link?.name)) && pathname.includes(selectedOrg?.organizationId);
              return (
              <div style={{ padding: '0 0 8px 0'}}>
                <StyledLink
                  key={index}
                  to={handleOrgUrl(link?.url)}
                  style={{
                    fontSize: '14px',
                    color: (isActiveLink) ? '#414042' : css.colors.Progress,
                    fontWeight: (isActiveLink) ? 'bold' : 'normal'
                  }}
                  activeLink={pathname.includes(handleOrgUrl(link?.url))}
                >
                  <LinkName name={link?.name} />
                </StyledLink>
                {link?.name.includes('Billing') && hasMultipleAccounts && showBillingAccounts &&
                  activeAccountsWithPermission.map(account => {
                    return (
                    <BillingAccountsDropdown
                      key={account.id}
                      billingAccount={account}
                      pathname={pathname}
                      organizationId={organizationId}
                      setActiveBillingAccount={setActiveBillingAccount}
                    />
                  )})                
                }
                {link?.name.includes('Order') && hasMultipleAccounts && showOrderAccounts &&
                  activeAccountsWithPermission.map(account => {
                    return (
                      <OrderHistoryDropdown
                        key={account.id}
                        billingAccount={account}
                        pathname={pathname}
                        organizationId={organizationId}
                        setActiveBillingAccount={setActiveBillingAccount}
                      />
                    )
                  })
                }
                {ENABLE_PCO_INTEGRATION && link?.name.includes('Groups') && showPCOGroups &&
                  <GroupDropdown
                    pathname={pathname}
                    organizationId={organizationId}
                  />
                  }
              </div>
            )})}
          </>
        )})}
      </div>
    </>
  );
}

function mapStateToProps(state) {
  const route = state.router.location.pathname;
  const orgRouteMatch = matchPath(route, { path: ORG_PATHS.BASE });
  const organizationId = orgRouteMatch?.params?.organizationId;

  return {
    organizationId
  };
}

const { string, object, bool, func } = PropTypes;

SideNavOrg.propTypes = {
  selectedOrg: object,
  updatedOrgMenuLinks: object,
  pathname: string,
  showBackBtn: bool,
  view: string,
  setView: func,
  organizationId: string
};

export default connect(mapStateToProps)(SideNavOrg);
